import { useCallback, useImperativeHandle } from "react";
import type { ReactNode, Ref } from "react";
import { useParams } from "react-router-dom";
import { GOOGLE_AUTH_REDIRECT_PATH } from "@common/constants";
import { getWebAuth } from "@lib/auth0";
import { getEnv } from "@lib/environment";
import { Button } from "@design-system/Button/Button";
import { GoogleIcon } from "@icons";

interface LoginWithGoogleButtonProps {
    variant: "primary" | "secondary";
    size?: "sm" | "md" | "lg" | "xl";
    block?: boolean;
    className?: string;
    children: ReactNode;
    rightIcon?: ReactNode;
}

export interface GoogleLoginHandle {
    login(): void;
}

interface RouteParams {
    inviteCode?: string;
}

export const LoginWithGoogleButton = ({
    children,
    size,
    ref,
    ...props
}: LoginWithGoogleButtonProps & { ref?: Ref<GoogleLoginHandle> }) => {
    const { inviteCode } = useParams() as Readonly<RouteParams>;

    const handleGoogleLogin = useCallback(() => {
        const redirect = new URL(window.location.origin);
        redirect.pathname = GOOGLE_AUTH_REDIRECT_PATH;

        if (inviteCode) {
            redirect.searchParams.set("inviteCode", inviteCode);
        }

        getWebAuth().authorize({
            connection: "google-oauth2",
            responseType: "token",
            redirectUri: redirect.toString(),
            audience: getEnv("VITE_AUTH0_AUDIENCE"),
        });
    }, [inviteCode]);

    useImperativeHandle(
        ref,
        () => ({
            login: handleGoogleLogin,
        }),
        [handleGoogleLogin],
    );

    return (
        <Button
            {...props}
            size={size ?? "md"}
            onClick={handleGoogleLogin}
            leftIcon={<GoogleIcon className="icon-color-revert" />}
        >
            {children}
        </Button>
    );
};
